import type { VoidFunctionComponent } from 'react';

import type { GitHubAppInstallation } from '../../../../../shared/model-typings';
import SimpleReactTable from '../../../generic/SimpleReactTable';
import TableHeader from '../../../generic/TableHeader';
import useGetGitHubAppInstallations from './useGetGitHubAppInstallations';
import Loader from '../../../generic/Loader';
import FallbackImage from '../../../generic/FallbackImage';
import DeleteGitHubAppButton from './DeleteGitHubAppButton';
import Flex from '../../../generic/Flex';

const columns = [
  {
    id: 'installationId',
    accessor: 'gitHubInstallationId',
    Header: (
      <TableHeader title="Installation ID" />
    ),
  },
  {
    id: 'name',
    accessor: 'name',
    Header: (
      <TableHeader title="Name" />
    ),
  },
  {
    id: 'type',
    accessor: 'accountType',
    Header: (
      <TableHeader title="Type" />
    ),
  },
  {
    id: 'createdAt',
    accessor: (i: GitHubAppInstallation) => i.createdAt.toISOString(),
    Header: (
      <TableHeader title="Created At" />
    ),
  },
  {
    id: 'remove',
    accessor: 'id',
    width: 75,
    Cell: (props: { value: number }) => (
      <Flex align="center" justify="center">
        <DeleteGitHubAppButton installationId={props.value} />
      </Flex>
    ),
    Header: (
      <TableHeader title="Remove" />
    ),
  },
];

const GitHubAppInstallationsTable: VoidFunctionComponent = () => {
  const { data: installations, error, isLoading } = useGetGitHubAppInstallations();

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <p className="alert alert-error">Unexpected error while fetching installations: { error.message }.</p>;
  }

  if (installations.results.length === 0) {
    return (
      <FallbackImage src="no-results" alt="No results" hasPadding>
        <p>You have no GitHub App installations.</p>
      </FallbackImage>
    );
  }

  return (
    <SimpleReactTable
      columns={columns}
      data={installations.results}
      defaultSortMode={[{ id: 'name', desc: false }]}
    />
  );
};

export default GitHubAppInstallationsTable;
